import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import DesignCanvas from './DesignCanvas';
import { FORMATS, IMAGE_ROLES, autoImage, autoText, fetchRecipes, type BrandKit, type KitPerson, type Lang, type Recipe, type Template } from '@/lib/brand-kit';

/** "Start from a ready design": proven cover layouts drawn live in THIS clinic's fonts and colours
 *  (research 2026-10-21). Picking one opens it as your own template to polish (owner 2026-10-24);
 *  a manager may choose "Add as base" instead, for everyone on the clinic. */
export default function RecipeGallery({ clientId, kit, people, onPick, onClose, canBase = false }: {
  clientId: string; kit: BrandKit; people: KitPerson[]; onPick: (t: Template, asBase: boolean) => void; onClose: () => void; canBase?: boolean;
}) {
  const [fmt, setFmt] = useState('story');
  const [asBase, setAsBase] = useState(false);
  const [lang, setLang] = useState<Lang>('en');
  const [list, setList] = useState<Recipe[] | null>(null);
  const f = FORMATS.find((x) => x.key === fmt) ?? FORMATS[1];
  useEffect(() => { setList(null); fetchRecipes(clientId, f.w, f.h).then(setList).catch(() => setList([])); }, [clientId, f.w, f.h]);
  const person = people.find((p) => p.info.shown !== false && p.info.photo) ?? people[0];
  // a sample photo so the previews read like real covers: the clinic's own reference or background
  const sample = kit.references[0]?.path || kit.templates.find((t) => t.background)?.background || '';
  // the first service names the treatment on a before and after preview (its photo cards stay empty: no patient photos here)
  const service = kit.services.find((s) => s.advertisable) ?? kit.services[0];

  return (
    <Card className="mb-4" data-testid="recipe-gallery">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-medium text-muted-strong">Start from a ready design</h3>
          <p className="text-xs text-muted">Layouts strong Instagram accounts use, built from this clinic's fonts and colours. Pick one, then make it yours.</p>
          {canBase && (
            <div className="flex gap-3 mt-2 text-xs text-muted-strong" data-testid="recipe-target">
              <label className="flex items-center gap-1.5"><input type="radio" checked={!asBase} onChange={() => setAsBase(false)} /> Add to My templates</label>
              <label className="flex items-center gap-1.5"><input type="radio" checked={asBase} onChange={() => setAsBase(true)} data-testid="recipe-as-base" /> Add as base (everyone on this clinic)</label>
            </div>
          )}
        </div>
        <div className="flex items-end gap-2">
          <div className="w-[22rem] max-w-full"><Select label="Format" value={fmt} onChange={setFmt} options={FORMATS.map((x) => ({ value: x.key, label: x.label }))} /></div>
          <div className="flex gap-1 pb-1">
            {(['en', 'ar'] as const).map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`px-3 h-8 rounded text-xs ${lang === l ? 'bg-accent-500/20 text-white' : 'text-muted'}`}>{l === 'en' ? 'English' : 'العربية'}</button>
            ))}
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center text-muted hover:text-white" aria-label="Close"><X size={16} /></button>
        </div>
      </div>
      {!list ? <p className="text-sm text-muted">Building the designs...</p> : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          {list.map((r) => {
            const texts: Record<string, string> = {}; const images: Record<string, string> = {};
            for (const e of r.template.elements) {
              if (IMAGE_ROLES.includes(e.role)) images[e.id] = e.role === 'image' ? sample : autoImage(kit, e, { lang, person });
              else if (e.role !== 'shape') texts[e.id] = autoText(kit, e, { lang, person, service })
                // a before and after's benefit line is empty by design; the preview shows where it goes
                || (r.template.purpose === 'before_after' && e.role === 'cta' ? (lang === 'ar' ? 'بشرة أنعم بثقة أكبر' : 'Smoother skin, more confidence') : '');
            }
            return (
              <button key={r.key} onClick={() => onPick(r.template, canBase && asBase)} data-testid={`recipe-${r.key}`}
                className="text-left rounded-lg border border-slate-800 hover:border-accent-400 p-2 transition group">
                <DesignCanvas clientId={clientId} kit={kit} template={r.template} lang={lang} texts={texts} images={images} />
                <div className="mt-2 text-sm text-muted-strong group-hover:text-white">{r.name}</div>
                <div className="text-[11px] text-muted leading-snug">{r.use}</div>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}
